import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Components
import { Form, Input } from 'top-component-library';

// Constants
import breakpoints from 'constants/breakpoints';

// Hooks
import { InlineChildren, Text } from '@doordash/design-language';
import { ItemSheetProviderContextProps } from 'contexts/ItemSheet';
import CartItem from 'models/CartItem';
import SharedCartItem from 'models/SharedCartItem';
import { OptionalGroup, RequiredGroup, StyledSpecialInstructionTitle, WarningIcon } from './styles';

function SpecialInstructions({
  cartItem,
  submitPressed,
  addHTMLElementError,
}: {
  cartItem: CartItem | SharedCartItem;
  submitPressed: boolean;
  addHTMLElementError: ItemSheetProviderContextProps['addHTMLElementError'];
}) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [touched, setTouched] = useState<boolean>(false);
  const { enabled, prompt, required } = cartItem.menuItem.special_instruction_config;

  const setSpecialInstructions = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    runInAction(() => {
      cartItem.special_instructions = value;
    });
  };

  const isError = required && !cartItem.special_instructions;

  useEffect(() => {
    if (isError && sectionRef.current) {
      addHTMLElementError(sectionRef.current);
    }
  }, [isError, sectionRef]);

  if (!enabled) {
    return null;
  }

  const GroupRequirement = required ? RequiredGroup : OptionalGroup;

  const hasInstructions = cartItem.special_instructions ? 'success' : 'error';
  const validateStatus = touched && required ? hasInstructions : '';

  return (
    <Container ref={sectionRef}>
      <Header>
        <StyledSpecialInstructionTitle styles={Text.Styles.Bold12}>
          {prompt || 'SPECIAL INSTRUCTIONS'}
        </StyledSpecialInstructionTitle>
        <GroupRequirement className={isError && submitPressed ? 'error' : ''}>
          {required && <WarningIcon />}
          <Text styles={Text.Styles.Medium12} color={required ? '#643B00' : Text.Colors.SystemBlack}>
            {required ? 'Required' : 'Optional'}
          </Text>
        </GroupRequirement>
      </Header>
      <Body>
        <Form.Item validateStatus={validateStatus} hasFeedback={required}>
          {/* Use TextArea because Ant Design's Input doesn't have showCount. */}
          <TextArea
            autoSize
            data-testid="special-instructions"
            onBlur={() => setTouched(true)}
            onChange={setSpecialInstructions}
            maxLength={30} // Limit set by railbot_app's OrderItem model.
            ref={inputRef}
            showCount
            size="large"
            value={cartItem.special_instructions}
          />
        </Form.Item>
      </Body>
    </Container>
  );
}

const Body = styled.div`
  // Validation icon
  && .ant-form-item-children-icon {
    margin-top: -15px;
  }
`;

const Container = styled.div`
  border-top: 6px solid #f7f7f7;
  flex: 1;
  padding: 1.25rem 1.25rem;

  @media (min-width: ${breakpoints.md}px) {
    border-top: 0;
    margin-top: 1.75rem;
    margin-left: 1.5rem;
    padding: 0;
  }
`;

const Header = styled(InlineChildren)`
  color: var(--color-neutral__title-active);
  justify-content: space-between;
  border-bottom: 1px solid var(--color-neutral__line);
  display: flex;
  flex-direction: row;
  font-size: 0.75rem;
  justify-content: space-between;
  margin: 0 0 1.25rem;
  padding-bottom: 0.5rem;

  span:nth-of-type(2) {
    opacity: 0.7;
  }

  .error {
    color: var(--color-error__regular);
  }
`;

const TextArea = styled(Input.TextArea)`
  background-color: var(--color-neutral__input-background); // Not the actual background color

  margin-bottom: 16px; // Spacing for the input field count text underneath

  // 'maxLength' counter
  &&::after {
    color: var(--color-neutral__placeholder);
    font-size: 0.75rem;
    font-weight: 500;
    margin-bottom: -0.625rem;
    margin-top: 0.125rem;
  }

  textarea {
    font-size: 0.75rem;
    padding: 0.625rem 1rem;
    background-color: var(--bose-item__modifier-textarea-background);
    border-color: var(--bose-item__modifier-textarea-border);
    color: var(--bose-item__modifier-textarea-text);

    // The use of placeholder text is discouraged due to how customizable
    // 'prompt' may be, but leaving these styles here just in case.
    &::placeholder {
      color: var(--bose-item__modifier-textarea-placeholder);
      font-size: 0.75rem;
    }

    &:focus,
    &:hover {
      border-color: var(--bose-item__modifier-textarea-border);
    }
  }
`;

export default observer(SpecialInstructions);
